// TD-1976 Phase 4 — reusable MFA setup form.
//
// Three sub-states driven by `authStore.mfaSetupStep`:
//   - 'setup'   → QR code + manual secret + Copy + "I've added it — continue →"
//   - 'verify'  → 6-digit OtpInput + "Verify and continue"
//   - 'success' → MFA enabled + session refresh indicator + Continue button
//
// Reused contexts:
//   - <MfaSetup /> page renders this with enforced=true (blocker banner shown,
//     no Cancel button — user can't leave until verify succeeds).
//   - Settings → Security → Enable MFA (Phase 6) will render this with
//     enforced=false (no banner; provides its own Cancel handler).

import { observer, useLocalObservable } from 'mobx-react';
import { useEffect, useState } from 'react';
import { runInAction } from 'mobx';
import { QRCodeSVG } from 'qrcode.react';
import { useStore } from '../../../../store/store';
import OtpInput from '../../../../components/inputs/OtpInput';
import MfaLockCountdown from './MfaLockCountdown';
import { MFA_OTP_REGEX } from '../../../../utils/constants/fields';

const OTP_LENGTH = 6;

/**
 * Render the secretCode with a thin-space every 4 characters for legibility.
 * Cognito returns a base32 string (e.g. JBSWY3DPEHPK3PXP); rendering it as
 * 4-char groups matches the mockup and most authenticator app UIs.
 */
const formatSecretForDisplay = (secret) => {
  if (!secret) return '';
  return secret.replace(/(.{4})/g, '$1 ').trim();
};

const SetupSubState = observer(({ enforced }) => {
  const { authStore } = useStore();
  const secret = authStore.mfaSetupSecret;
  const otpauthUri = secret?.otpauthUri || '';
  const secretCode = secret?.secretCode || '';

  // Copy-status feedback. Three states because the user needs different
  // affordances per outcome:
  //   - 'idle'   → "Copy" label
  //   - 'copied' → "Copied" label (auto-resets after ~2.5s)
  //   - 'failed' → "Try manual select" label + a hint line pointing at the
  //                rendered key. Clipboard API can fail on HTTP origins,
  //                older browsers, or when permission is denied; failing
  //                silently leaves the user thinking they have the key
  //                copied when they don't, which is the actual lockout
  //                path. The hint never echoes the secret value.
  const [copyStatus, setCopyStatus] = useState('idle');

  useEffect(() => {
    if (copyStatus === 'idle') return undefined;
    const t = setTimeout(() => setCopyStatus('idle'), 2500);
    return () => clearTimeout(t);
  }, [copyStatus]);

  const onCopy = async () => {
    if (!secretCode) return;
    if (!navigator.clipboard) {
      setCopyStatus('failed');
      return;
    }
    try {
      await navigator.clipboard.writeText(secretCode);
      setCopyStatus('copied');
    } catch {
      setCopyStatus('failed');
    }
  };

  if (authStore.isAuthenticating && !secret) {
    return <p className="lede">Preparing your MFA setup…</p>;
  }

  if (authStore.mfaSetupError && !secret) {
    return (
      <>
        <div className="error">{authStore.mfaSetupError}</div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => authStore.startMfaSetup()}>
          Try again
        </button>
      </>
    );
  }

  return (
    <>
      <h2>Set up multi-factor authentication</h2>
      <p className="lede">
        Scan the QR code with an authenticator app, or enter the setup key manually.
        We support <b>Google Authenticator</b>, <b>Microsoft Authenticator</b>,{' '}
        <b>1Password</b>, and <b>Authy</b>.
      </p>

      <div className="mfa-qr-wrap">
        {otpauthUri ? (
          <QRCodeSVG value={otpauthUri} size={180} includeMargin level="M" />
        ) : (
          <div className="mfa-qr-placeholder" />
        )}
        <div className="small mt-2">Scan with your authenticator app</div>
      </div>

      <div className="mfa-secret-section">
        <div className="mfa-secret-label">Or enter this setup key manually</div>
        <div className="mfa-secret-block">
          <span className="mfa-secret-value">{formatSecretForDisplay(secretCode)}</span>
          <button
            type="button"
            onClick={onCopy}
            disabled={!secretCode}
            aria-live="polite">
            {copyStatus === 'copied' && 'Copied'}
            {copyStatus === 'failed' && 'Try manual select'}
            {copyStatus === 'idle' && 'Copy'}
          </button>
        </div>
        {copyStatus === 'failed' && (
          <output className="mfa-copy-hint">
            Couldn&rsquo;t copy automatically. Select the key above and copy it
            with ⌘C / Ctrl-C.
          </output>
        )}
      </div>

      <div className="mfa-row">
        <div className="small">Already added it?</div>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!secretCode}
          onClick={() => authStore.setMfaSetupStep('verify')}>
          I&rsquo;ve added it — continue →
        </button>
      </div>

      {enforced ? null : (
        <p className="small mt-2">
          You can come back to this anytime from Settings → Security.
        </p>
      )}
    </>
  );
});

const VerifySubState = observer(() => {
  const { authStore } = useStore();

  const state = useLocalObservable(() => ({
    code: '',
    setCode(value) {
      runInAction(() => {
        state.code = value;
      });
    }
  }));

  // TD-2192: while the backend locks code checks, keep its text up even after
  // "Back to QR code" cleared mfaSetupError, and block entry until it lifts.
  const isLocked = authStore.isMfaCodeLocked;
  const errorText = authStore.mfaCodeLockMessage || authStore.mfaSetupError;
  const hasError = !!errorText;
  const isSubmitDisabled =
    !MFA_OTP_REGEX.test(state.code) || authStore.isAuthenticating || isLocked;

  const onSubmit = (e) => {
    e.preventDefault();
    if (isSubmitDisabled) return;
    authStore.verifyMfaSetup(state.code);
  };

  return (
    <form onSubmit={onSubmit}>
      <h2>Verify your authenticator</h2>
      <p className="lede">
        Enter the 6-digit code shown in your authenticator app. The code refreshes
        every 30 seconds.
      </p>

      <div className="form-group">
        <label htmlFor="mfa-setup-verify-0">Authentication code</label>
        <OtpInput
          length={OTP_LENGTH}
          value={state.code}
          onChange={state.setCode}
          onComplete={state.setCode}
          error={hasError}
          disabled={authStore.isAuthenticating || isLocked}
          autoFocus
          name="mfa-setup-verify"
        />
        <div className="small mt-2">Code refreshes every 30 seconds.</div>
      </div>

      {hasError && <div className="error">{errorText}</div>}
      <MfaLockCountdown />

      <button
        type="submit"
        className="btn btn-primary btn-block"
        disabled={isSubmitDisabled}>
        {authStore.isAuthenticating ? 'Verifying…' : 'Verify and continue'}
      </button>

      <div className="alert alert-info mfa-info-alert">
        <b>Heads up:</b> after a successful verify we will refresh your session
        to clear the setup-required flag. You may see a brief loading state.
      </div>

      <div className="row small mfa-help-row">
        {/* type="button": inside the code form, a submit would send the typed
            code and spend one of the 3 attempts before the lockout (TD-2192). */}
        <button
          type="button"
          className="link help-link"
          onClick={() => authStore.setMfaSetupStep('setup')}>
          ← Back to QR code
        </button>
      </div>
    </form>
  );
});

const SuccessSubState = observer(({ onComplete }) => {
  const { authStore } = useStore();
  const isRotation = authStore.mfaSetupMode === 'rotate';

  // Auto-advance once the refreshed session has landed. We hold the success
  // state visible for a beat so the user sees the confirmation, then call
  // onComplete — the page picks the destination (for enroll: route guard
  // releases; for rotation: caller returns to the Security status view).
  useEffect(() => {
    const t = setTimeout(() => onComplete?.(), 1200);
    return () => clearTimeout(t);
  }, [onComplete]);

  return (
    <div className="mfa-success">
      <div className="mfa-success-icon">✓</div>
      <h2>{isRotation ? 'Setup key updated' : 'MFA enabled'}</h2>
      <p className="lede">
        {isRotation ? (
          <>
            Your new authenticator is now linked. The previous setup key is no
            longer valid — sign-in from now on uses the new one.
          </>
        ) : (
          <>
            From now on we&rsquo;ll ask for a 6-digit code every time you sign in.
            Keep your authenticator app safe — it&rsquo;s the only way to reach
            your account.
          </>
        )}
      </p>

      <div className="alert alert-success mfa-info-alert">
        <b>Refreshing your session…</b> We&rsquo;re updating your ID token so the
        rest of the app stays in sync.
      </div>

      <button
        type="button"
        className="btn btn-primary btn-block mt-4"
        onClick={() => onComplete?.()}>
        Continue to transact.digital
      </button>
    </div>
  );
});

/**
 * @param {object} props
 * @param {boolean} [props.enforced]   true → render amber blocker banner; user
 *                                     must complete setup to leave the screen.
 * @param {() => void} [props.onComplete]  Called after the success sub-state
 *                                          finishes (either auto-advance or
 *                                          user clicked Continue).
 */
const MfaSetupForm = observer(({ enforced = false, onComplete }) => {
  const { authStore } = useStore();

  // Kick off the setup request on mount when we're in the 'setup' sub-state
  // and don't already have the secret. clearMfaSetup on unmount drops the
  // secret payload so it never lingers in memory across navigations.
  useEffect(() => {
    if (authStore.mfaSetupStep === 'setup' && !authStore.mfaSetupSecret) {
      authStore.startMfaSetup();
    }
    return () => {
      authStore.clearMfaSetup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const step = authStore.mfaSetupStep;

  return (
    <div className="mfa-setup-form">
      {enforced && step !== 'success' && (
        <div className="blocker-banner">
          <span className="key">🔒 Security required</span>
          <span>
            Your administrator requires multi-factor authentication before you
            can access transact.digital. You will not be able to leave this
            screen until setup is complete.
          </span>
        </div>
      )}

      {step === 'setup' && <SetupSubState enforced={enforced} />}
      {step === 'verify' && <VerifySubState />}
      {step === 'success' && <SuccessSubState onComplete={onComplete} />}
    </div>
  );
});

export default MfaSetupForm;
